import type { FC, ReactNode } from 'react'

import {
    useEffect,
    useId,
    useRef,
    useState
} from 'react'
import { gsap, ScrollTrigger } from '@/lib/gsap'
import { prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'

export interface AccordionItem {
    title: ReactNode
    content: ReactNode
}

interface AccordionProps {
    items: Array<AccordionItem>
    className?: string
    titleClassName?: string
    defaultOpen?: number | null
}

interface RowProps {
    item: AccordionItem
    open: boolean
    onToggle: () => void
    titleClassName?: string
}

const Row: FC<RowProps> = ({ item, open, onToggle, titleClassName }): ReactNode => {
    const id = useId()
    const panel = useRef<HTMLDivElement>(null)
    const initiallyOpen = useRef(open)
    const mounted = useRef(false)

    useEffect(() => {
        const element = panel.current

        if (!element) return

        if (!mounted.current) {
            mounted.current = true

            return
        }

        gsap.to(element, {
            height: open ? 'auto' : 0,
            duration: prefersReducedMotion() ? 0 : 0.7,
            ease: 'expo.out',
            overwrite: true,
            onComplete: () => ScrollTrigger.refresh()
        })
    }, [open])

    return (
        <div className='border-b border-line'>
            <h3>
                <button
                    type='button'
                    id={`${id}-trigger`}
                    aria-expanded={open}
                    aria-controls={`${id}-panel`}
                    onClick={onToggle}
                    className={cn('group flex w-full items-center justify-between gap-6 py-5 text-left label-mono text-bone transition-colors duration-300 hover:text-acid', titleClassName)}
                >
                    <span>
                        {item.title}
                    </span>

                    <span aria-hidden='true' className='relative block size-3 shrink-0 text-acid'>
                        <span className='absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-current' />
                        <span className={cn('absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current transition-transform duration-500 ease-expo-out', open && 'scale-y-0')} />
                    </span>
                </button>
            </h3>

            <div
                ref={panel}
                id={`${id}-panel`}
                role='region'
                aria-labelledby={`${id}-trigger`}
                inert={!open}
                className='overflow-hidden'
                style={initiallyOpen.current ? undefined : { height: 0 }}
            >
                <div className='pb-6 pr-8 body-copy text-bone/70'>
                    {item.content}
                </div>
            </div>
        </div>
    )
}

/** Single-open disclosure list with eased height transitions. */
const Accordion: FC<AccordionProps> = ({ items, className, titleClassName, defaultOpen = null }): ReactNode => {
    const [openIndex, setOpenIndex] = useState<number | null>(defaultOpen)

    return (
        <div className={cn('border-t border-line', className)}>
            {items.map((item, index) => (
                <Row
                    key={index}
                    item={item}
                    open={openIndex === index}
                    titleClassName={titleClassName}
                    onToggle={() => setOpenIndex(current => current === index ? null : index)}
                />
            ))}
        </div>
    )
}

export default Accordion
